import { useRouter } from 'next/router';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import {
  ContentStyledComponent,
  ParagraphStyledComponentConstructor,
  TextStyledComponent,
  TextStyledComponentConstructor,
} from '@zg-rentals/backend-styled-components';

import { useGetListingRecommendations } from '../../../hooks/queries/properties';

import {
  EVENT_IDS as ADD_3D_TOURS_EVENT_IDS,
  emitPostingPathHirePhotographerEvent,
  emitPostingPathOurAppEvent,
} from '../../../analytics/events/add3DTours';
import SpinnerContainer from '../../SpinnerContainer';

import type { ReactNode } from 'react';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const OUR_APP = 'our app';
const HIRE_A_PHOTOGRAPHER = 'hire a photographer';

const CS_EVENTS_ID_MAP = {
  [OUR_APP]: ADD_3D_TOURS_EVENT_IDS.POSTING_PATH_OUR_APP,
  [HIRE_A_PHOTOGRAPHER]: ADD_3D_TOURS_EVENT_IDS.POSTING_PATH_HIRE_PHOTOGRAPHER,
};

type Props = {
  onClose: () => void;
  footer?: ReactNode;
  recommendationType: string;
  listing?: ZrmClickstreamListing;
  user?: ZrmClickstreamUser;
};

const RecommendationModal = ({ onClose, recommendationType, footer, listing, user }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { recommendationsData, isFetching } = useGetListingRecommendations({ listingAlias });

  const recommendation = recommendationsData?.recommendations?.find((rec) => rec.type === recommendationType);

  const title = recommendation?.learnMore?.title
    ? TextStyledComponentConstructor(recommendation?.learnMore?.title)
    : TextStyledComponentConstructor({ text: 'Recommendation' });
  const content = recommendation?.learnMore?.content
    ? recommendation?.learnMore?.content.map((c) => ParagraphStyledComponentConstructor(c))
    : [];

  if (!isFetching && !recommendation) {
    return null;
  }

  return (
    <Modal
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={
        isFetching ? undefined : (
          <Heading level={2}>
            <TextStyledComponent styledComponentContent={{ ...title, type: 'text' }} />
          </Heading>
        )
      }
      body={
        <Box css={{ marginBottom: '600' }}>
          <Box css={{ marginBlock: '200' }}>
            {isFetching ? (
              <SpinnerContainer />
            ) : (
              <ContentStyledComponent
                title={{ ...title, text: '' }}
                content={content}
                csEventsHandler={
                  title?.text === 'Add a 3D tour'
                    ? ({ text }: { text: string }) => {
                        if (text === OUR_APP && user && listing) {
                          emitPostingPathOurAppEvent({ currentProperty: listing, user });
                          return;
                        }

                        if (text === HIRE_A_PHOTOGRAPHER && user && listing) {
                          emitPostingPathHirePhotographerEvent({ currentProperty: listing, user });
                          return;
                        }
                      }
                    : undefined
                }
                csEventsIdMap={CS_EVENTS_ID_MAP}
              />
            )}
          </Box>
        </Box>
      }
      footer={footer}
    />
  );
};

export default RecommendationModal;
